import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend 
} from 'recharts';
import { 
  ArrowLeft, Radar, MapPin, Database, Crosshair, 
  ShieldAlert, Zap, Layers, Info, CheckCircle, ExternalLink, FileDown
} from 'lucide-react';
import { exportScanToPdf } from '../services/scanPdfExport';
import axios from 'axios';
import { useAuth } from '../store/AuthContext';
import { getApiUrl } from '../services/api';
import MapContainer from '../components/map/MapContainer';

const ScanDetails = ({ searchQuery, searchTrigger }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCat, setSelectedCat] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'comp', 'eng'
  const [exporting, setExporting] = useState(false);

  // Intelligent Context (Competence/Engines)
  const [intel, setIntel] = useState({ competence: [], engines: [] });

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        const [scanResp, meResp] = await Promise.all([
          axios.get(`${getApiUrl()}/scout/scan/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${getApiUrl()}/auth/me`,           { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setScan(scanResp.data);
        const mp = meResp.data.data?.user?.missionProfile;
        if (mp) {
          setIntel({
            competence: (mp.competence || []).map(c => typeof c === 'string' ? c : c.name),
            engines:    (mp.engines    || []).map(e => typeof e === 'string' ? e : e.name)
          });
        }
      } catch (err) {
        setError('Failed to retrieve mission telemetry data.');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [id, token]);

  if (loading) return (
    <div className="nasa-bg" style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2, ease: "linear" }}>
        <Radar size={48} color="var(--tech-cyan)" />
      </motion.div>
    </div>
  );

  if (error || !scan) return (
    <div className="nasa-bg" style={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
      <ShieldAlert size={64} color="var(--mars-red)" />
      <h2 style={{ marginTop: '20px' }}>{error || 'SCAN DATA CORRUPTED'}</h2>
      <button className="nasa-button" onClick={() => navigate(-1)}>RETURN TO DASHBOARD</button>
    </div>
  );

  // Data Processing for Charts
  const rawAllPois = Object.values(scan.results || {}).flat();

  // Normalize: lowercase + strip accents + remove everything non-alphanumeric
  const norm = (str) =>
    (str || '')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9 ]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  // Check if a POI matches a brand name using multi-strategy fuzzy matching
  const matchesBrand = (poi, brandName) => {
    const nb = norm(brandName);
    if (!nb || nb.length < 2) return false;

    // All OSM fields to check
    const candidates = [
      norm(poi.name),
      norm(poi.tags?.brand),
      norm(poi.tags?.operator),
      norm(poi.tags?.['brand:en']),
      norm(poi.tags?.['name:en']),
    ].filter(Boolean);

    for (const c of candidates) {
      if (!c) continue;
      // substring in both directions
      if (c.includes(nb) || nb.includes(c)) return true;
      // word-level: any significant word of brand matches
      const bWords = nb.split(' ').filter(w => w.length > 2);
      const cWords = c.split(' ').filter(w => w.length > 2);
      if (bWords.length && bWords.some(bw => cWords.includes(bw))) return true;
    }
    return false;
  };

  const identifyIntel = (poi) => {
    const isComp = intel.competence?.some(c => matchesBrand(poi, c));
    const isEng  = intel.engines?.some(e  => matchesBrand(poi, e));
    return { isComp, isEng };
  };

  const filteredPois = rawAllPois.filter(p => {
    const { isComp, isEng } = identifyIntel(p);

    if (activeFilter === 'comp') return isComp;
    if (activeFilter === 'eng') return isEng;
    return true;
  });

  const totals = filteredPois.reduce((acc, p) => {
    const { isComp, isEng } = identifyIntel(p);
    if (isComp) acc.compTotal++;
    if (isEng) acc.engTotal++;
    return acc;
  }, { compTotal: 0, engTotal: 0 });

  const COLORS = ['#00B8D9', '#36B37E', '#FF5630', '#FFAB00', '#6554C0'];
  const activeColor = activeFilter === 'comp' ? '#E27B58' : activeFilter === 'eng' ? '#2ECC71' : '#00CEC9';

  const pieData = Object.entries(
    filteredPois.reduce((acc, p) => {
      acc[p.category] = (acc[p.category] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  })).sort((a,b) => b.value - a.value);

  // Distance Buckets
  const distanceBuckets = [
    { name: '0-100m', count: filteredPois.filter(p => p.distance <= 100).length },
    { name: '100-200m', count: filteredPois.filter(p => p.distance > 100 && p.distance <= 200).length },
    { name: '200-300m', count: filteredPois.filter(p => p.distance > 200 && p.distance <= 300).length },
    { name: '300m+', count: filteredPois.filter(p => p.distance > 300).length }
  ];

  const neutralCount = filteredPois.length - totals.compTotal - totals.engTotal;
  const intelChartData = [
    { name: 'Competence', value: totals.compTotal, color: '#E27B58' },
    { name: 'Engines', value: totals.engTotal, color: '#2ECC71' },
    { name: 'Neutral', value: neutralCount, color: '#0B3D91' }
  ].filter(d => d.value > 0);

  const handleExport = async () => {
    setExporting(true);
    try {
      exportScanToPdf({ scan, intel, rawAllPois, identifyIntel });
    } finally {
      setTimeout(() => setExporting(false), 1500);
    }
  };

  return (
    <div className="nasa-bg" style={{ minHeight: 'calc(100vh - 60px)', width: '100%', display: 'flex', flexDirection: 'column' }}>
      
      {/* Header - Compacted */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '20px 30px 15px 30px', 
        flexShrink: 0,
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button 
            className="nasa-button" 
            style={{ borderRadius: '50%', padding: '10px' }}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 style={{ margin: 0, textTransform: 'uppercase', letterSpacing: '3px', color: 'var(--tech-cyan)', fontSize: '1.5rem' }}>
              Strategic Area Scan
            </h1>
            <p style={{ margin: 0, opacity: 0.6, fontSize: '0.7rem', fontFamily: 'monospace' }}>
              MISSION ID: {scan._id.slice(-8).toUpperCase()} | TARGET: {scan.referenceId?.name || "GEOCOORD-LOC"}
            </p>
          </div>
        </div>
        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
           <div style={{ color: 'var(--mars-red)', fontWeight: 'bold', fontSize: '1.1rem', letterSpacing: '1px' }}>TACTICAL REPORT</div>
           <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>{new Date(scan.scanAt).toLocaleString()}</div>
           <button
             className="nasa-button"
             onClick={handleExport}
             disabled={exporting}
             style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.7rem', padding: '8px 14px', opacity: exporting ? 0.6 : 1 }}
           >
             <FileDown size={15} />
             {exporting ? 'GENERATING...' : 'EXPORT PDF'}
           </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        
        {/* Row 1: Key Metrics - Compacted */}
        <div style={{ padding: '0 30px 20px 30px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
          <motion.div 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveFilter('all')}
            className="mission-control-card" 
            style={{ 
              padding: '15px', 
              textAlign: 'center', 
              cursor: 'pointer',
              borderColor: activeFilter === 'all' ? 'var(--tech-cyan)' : 'rgba(0, 240, 255, 0.1)',
              background: activeFilter === 'all' ? 'rgba(0, 240, 255, 0.05)' : 'transparent',
              transition: 'all 0.3s ease'
            }}
          >
            <Database size={20} color="var(--tech-cyan)" style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--tech-cyan)' }}>{filteredPois.length}</div>
            <div style={{ fontSize: '0.65rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '1px' }}>Entities Found</div>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveFilter('comp')}
            className="mission-control-card" 
            style={{ 
              padding: '15px', 
              textAlign: 'center', 
              cursor: 'pointer',
              borderColor: activeFilter === 'comp' ? 'var(--mars-red)' : 'rgba(226, 123, 88, 0.2)',
              background: activeFilter === 'comp' ? 'rgba(226, 123, 88, 0.1)' : 'transparent',
              transition: 'all 0.3s ease'
            }}
          >
            <ShieldAlert size={20} color="var(--mars-red)" style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--mars-red)' }}>{totals.compTotal}</div>
            <div style={{ fontSize: '0.65rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '1px' }}>Enemy Competitors</div>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveFilter('eng')}
            className="mission-control-card" 
            style={{ 
              padding: '15px', 
              textAlign: 'center', 
              cursor: 'pointer',
              borderColor: activeFilter === 'eng' ? 'var(--bio-green)' : 'rgba(46, 204, 113, 0.2)',
              background: activeFilter === 'eng' ? 'rgba(46, 204, 113, 0.1)' : 'transparent',
              transition: 'all 0.3s ease'
            }}
          >
            <Zap size={20} color="var(--bio-green)" style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--bio-green)' }}>{totals.engTotal}</div>
            <div style={{ fontSize: '0.65rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '1px' }}>Strategic Engines</div>
          </motion.div>
        </div>

        {/* Row 2: Charts Row - Spaced and Compacted */}
        <div style={{ padding: '0 30px 20px 30px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '60px' }}>
          <div className="mission-control-card" style={{ height: '300px', display: 'flex', flexDirection: 'column', borderColor: activeColor, maxWidth: 'none' }}>
            <h4 style={{ margin: '0 0 15px 0', fontSize: '0.8rem', color: activeColor, letterSpacing: '1px' }}>DENSITY BY DISTANCE</h4>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distanceBuckets} margin={{ top: 10, right: 30, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="name" stroke="rgba(255,255,255,0.5)" style={{ fontSize: '11px' }} />
                <YAxis stroke="rgba(255,255,255,0.5)" style={{ fontSize: '11px' }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'rgba(5, 7, 10, 0.95)', border: `1px solid ${activeColor}`, color: '#fff' }}
                  itemStyle={{ color: activeColor }}
                />
                <Bar dataKey="count" fill={activeColor} radius={[4, 4, 0, 0]}>
                  {distanceBuckets.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={activeColor} fillOpacity={0.8} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mission-control-card" style={{ height: '300px', display: 'flex', flexDirection: 'column', borderColor: activeColor, maxWidth: 'none', position: 'relative' }}>
             <h4 style={{ margin: '0 0 15px 0', fontSize: '0.8rem', color: activeColor, letterSpacing: '1px' }}>CATEGORICAL MIX</h4>
             <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  nameKey="name"
                  dataKey="value"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  labelLine={false}
                  isAnimationActive={true}
                >
                  {pieData.map((entry, index) => {
                    const palettes = {
                      comp: ['#E27B58', '#FF9F7F', '#C45D3B', '#FF7D4C'],
                      eng: ['#2ECC71', '#4DFF90', '#27AE60', '#1E8449'],
                      all: ['#00CEC9', '#0984E3', '#0B3D91', '#6C5CE7', '#FAB1A0']
                    };
                    const currentPalette = palettes[activeFilter] || palettes.all;
                    return <Cell key={`cell-${index}`} fill={currentPalette[index % currentPalette.length]} />;
                  })}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: 'rgba(5, 7, 10, 0.95)', border: `1px solid ${activeColor}`, borderRadius: '4px', color: '#fff' }}
                  itemStyle={{ fontSize: '12px', color: activeColor }}
                  labelStyle={{ color: '#fff', fontSize: '11px' }}
                />
                <Legend verticalAlign="bottom" align="center" iconSize={10} wrapperStyle={{ fontSize: '10px', paddingTop: '15px', color: 'rgba(255,255,255,0.7)' }} />
              </PieChart>
            </ResponsiveContainer>
            {pieData.length === 0 && (
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '0.7rem', opacity: 0.5 }}>
                NO DATA FOR THIS SELECTION
              </div>
            )}
          </div>

          <div className="mission-control-card" style={{ height: '300px', display: 'flex', flexDirection: 'column', borderColor: activeFilter === 'all' ? 'rgba(255,255,255,0.1)' : activeColor, maxWidth: 'none', position: 'relative' }}>
             <h4 style={{ margin: '0 0 15px 0', fontSize: '0.8rem', color: activeFilter === 'all' ? '#fff' : activeColor, letterSpacing: '1px' }}>INTEL BALANCE</h4>
             <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={intelChartData}
                  cx="50%"
                  cy="50%"
                  nameKey="name"
                  dataKey="value"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  labelLine={false}
                  isAnimationActive={true}
                >
                  {intelChartData.map((entry, idx) => (
                    <Cell 
                      key={`cell-${idx}`} 
                      fill={entry.color} 
                      opacity={activeFilter === 'all' || (activeFilter === 'comp' && entry.name === 'Competence') || (activeFilter === 'eng' && entry.name === 'Engines') ? 1 : 0.2} 
                    />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: 'rgba(5, 7, 10, 0.95)', border: `1px solid ${activeColor}`, borderRadius: '4px', color: '#fff' }}
                  itemStyle={{ fontSize: '12px', color: activeColor }}
                  labelStyle={{ color: '#fff', fontSize: '11px' }}
                />
                <Legend verticalAlign="bottom" align="center" iconSize={10} wrapperStyle={{ fontSize: '10px', paddingTop: '15px', color: 'rgba(255,255,255,0.7)' }} />
              </PieChart>
            </ResponsiveContainer>
            {intelChartData.length === 0 && (
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '0.7rem', opacity: 0.5 }}>
                NO INTEL DATA
              </div>
            )}
          </div>
        </div>


        {/* Row 3: THE MAP */}
        <div style={{ width: '95%', height: '520px', minHeight: '450px', position: 'relative', margin: '15px auto' }}>
           <MapContainer 
             opportunities={scan.referenceId ? [scan.referenceId] : []}
             scoutResults={filteredPois.map(p => {
               const { isComp, isEng } = identifyIntel(p);
               return {
                 id: p.id,
                 lat: p.lat,
                 lng: p.lng,
                 name: p.name,
                 color: isComp ? '#FF4136' : isEng ? '#2ECC71' : '#00CEC9'
               };
             })}
             scanCircle={{
               center: (scan.center && scan.center.coordinates) ? { 
                 lat: scan.center.coordinates[1], 
                 lng: scan.center.coordinates[0] 
               } : { lat: 0, lng: 0 },
               radius: scan.radius,
               visible: true
             }}
             addressToSearch={searchQuery}
             searchTrigger={searchTrigger}
             style={{ height: '100%', width: '100%' }}
           />
           
           {/* Map Legend Overlay */}
           <div style={{
             position: 'absolute',
             top: '20px',
             right: '20px',
             background: 'rgba(5, 7, 10, 0.85)',
             border: `1px solid ${activeColor}`,
             padding: '15px',
             borderRadius: '4px',
             fontSize: '0.7rem',
             backdropFilter: 'blur(8px)',
             zIndex: 10
           }}>
              <div style={{ marginBottom: '10px', color: activeColor, fontWeight: 'bold' }}>TACTICAL OVERLAY</div>
              { (activeFilter === 'all' || activeFilter === 'comp') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#FF4136', boxShadow: '0 0 8px #FF4136' }}></div>
                  <span>ENEMY COMPETITION</span>
                </div>
              )}
              { (activeFilter === 'all' || activeFilter === 'eng') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#2ECC71', boxShadow: '0 0 8px #2ECC71' }}></div>
                  <span>STRATEGIC ENGINES</span>
                </div>
              )}
              { activeFilter === 'all' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#00CEC9', boxShadow: '0 0 8px #00CEC9' }}></div>
                  <span>NEUTRAL POI</span>
                </div>
              )}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '10px', fontSize: '0.6rem', opacity: 0.8 }}>
                PERIMETER: {scan.radius} METERS
              </div>
           </div>
        </div>

        {/* Row 4: Entity Reconnaissance - Grouped by Category */}
        <div style={{ padding: '0 30px 40px 30px' }}>
          <div className="mission-control-card" style={{ padding: '20px', borderColor: activeColor }}>
            <h4 style={{ margin: '0 0 20px 0', fontSize: '0.9rem', color: activeColor, display: 'flex', alignItems: 'center', gap: '10px', letterSpacing: '1px' }}>
              <Layers size={18} /> DETAILED ENTITY RECONNAISSANCE ({filteredPois.length})
            </h4>

            {(() => {
              // Group by category
              const grouped = filteredPois
                .slice()
                .sort((a, b) => a.distance - b.distance)
                .reduce((acc, p) => {
                  const key = p.category || 'others';
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(p);
                  return acc;
                }, {});

              const categoryIcons = {
                restaurants: '🍽', banks: '🏦', leisure: '🎭',
                schools: '🎓', others: '📍'
              };

              return Object.entries(grouped).map(([category, pois]) => (
                <div key={category} style={{ marginBottom: '24px' }}>
                  {/* Group Header */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    marginBottom: '10px', paddingBottom: '6px',
                    borderBottom: `1px solid ${activeColor}44`
                  }}>
                    <span style={{ fontSize: '1rem' }}>{categoryIcons[category] || '📍'}</span>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '2px', color: activeColor }}>
                      {category} <span style={{ opacity: 0.7, fontFamily: 'monospace' }}>({pois.length})</span>
                    </span>
                  </div>

                  {/* Items Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px' }}>
                    {pois.map((p, idx) => {
                      const { isComp, isEng } = identifyIntel(p);
                      const itemColor = isComp ? 'var(--mars-red)' : isEng ? 'var(--bio-green)' : 'rgba(255,255,255,0.6)';
                      return (
                        <div
                          key={idx}
                          style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '9px 12px',
                            background: isComp ? 'rgba(226,123,88,0.05)' : isEng ? 'rgba(46,204,113,0.05)' : 'rgba(255,255,255,0.02)',
                            border: `1px solid ${isComp ? 'rgba(226,123,88,0.3)' : isEng ? 'rgba(46,204,113,0.3)' : 'rgba(255,255,255,0.08)'}`,
                            borderRadius: '4px'
                          }}
                        >
                          <div style={{ overflow: 'hidden', flex: 1 }}>
                            <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', fontSize: '0.82rem', color: itemColor }}>
                              {p.name}
                            </div>
                          </div>
                          <div style={{ flexShrink: 0, marginLeft: '12px', fontFamily: 'monospace', fontSize: '0.75rem', color: activeColor, fontWeight: 'bold' }}>
                            {p.distance}m
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
          </div>
          
          {/* Footer Logs - Full Width */}
          <div className="mission-control-card" style={{ padding: '15px 30px', opacity: 0.6, marginTop: '20px' }}>
             <div style={{ fontSize: '0.7rem', fontFamily: 'monospace', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                <span>[SYSTEM_LOG] Scan completed. Telemetry data synced at {new Date(scan.scanAt).toISOString()}</span>
                <span>
                  GEOLOC: {(scan.center && scan.center.coordinates) 
                    ? `${scan.center.coordinates[1].toFixed(6)}, ${scan.center.coordinates[0].toFixed(6)}` 
                    : 'UNKNOWN_ORBIT'}
                </span>
             </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ScanDetails;
